import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@components/common/alert-dialog";
import { toast } from "@pma/hooks/use-toast";
import useTranslator from "@pma/hooks/use-translator";
import { DictType } from "@pma/types";
import { EDIT_BILL_ENDPOINT, FINANCIAL_SERVER_URL } from "@pma/utils/api-routes";
import { ACCESS_TOKEN } from "@pma/utils/constants";
import { useMutation } from "@tanstack/react-query";
import { getFinancialErrorMessage } from "@pma/utils/helpers";
import axios from "axios";
import { parseCookies } from "nookies";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@components/common/button";
import { Trash2 } from "lucide-react";
import { Dispatch, SetStateAction } from "react";

interface Props {
  selectedItems: string[];
  bill: DictType;
  setSelectedItems: Dispatch<SetStateAction<any>>;
}
const DeleteBillItems = ({ selectedItems, bill, setSelectedItems }: Props) => {
  const { translate, locale } = useTranslator();

  const navigate = useNavigate();
  const [searchParam] = useSearchParams();

  const isHebrew = locale === "he";
  const newParams = new URLSearchParams(searchParam);

  const billId = bill?.id;
  const billItems = bill?._bill_items;

  const accessToken = parseCookies()[ACCESS_TOKEN];

  const deleteBillItem = async () => {
    const payload = billItems
      ?.filter((item: DictType) => {
        return selectedItems.includes(item?.boq_item_id?.toString());
      })
      .map((item: DictType) => ({
        bill_item_id: item?.id,
        amount: item?.amount,
        delete: true,
        message_text: "",
      }));

    const response = await axios.post(
      FINANCIAL_SERVER_URL + EDIT_BILL_ENDPOINT,
      {
        bill_id: billId,
        items: payload,
      },
      {
        headers: {
          Authorization: "Bearer " + accessToken,
          "Content-Type": "application/json",
        },
      },
    );
    return response.data;
  };

  const handleDeleteBillItemMutation = useMutation({
    mutationKey: ["delete-bill-item"],
    mutationFn: deleteBillItem,
    onSuccess: () => {
      toast({
        title: translate("Success"),
        description: translate("bill items deleted successfully"),
        variant: "success",
      });

      newParams.set("revalidate", "bills");

      navigate(`?${newParams.toString()}`);
      setSelectedItems([]);
    },
    onError: (error: any) => {
      const errorResponse = error?.response?.data?.message;
      toast({
        title: translate("Error"),
        description: getFinancialErrorMessage(error, "delete bill items", translate),
        variant: "destructive",
      });
    },
  });

  return (
    <AlertDialog>
      <AlertDialogTrigger>
        <Button
          variant={"outline"}
          className='border-destructive text-destructive'
          size={"sm"}
          disabled={selectedItems?.length === 0}
        >
          <Trash2 className='h-4 w-4 mr-1' />
          {translate("Delete Selected")} {selectedItems?.length}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent dir={isHebrew ? "rtl" : "ltr"}>
        <AlertDialogHeader>
          <AlertDialogTitle>{translate("Delete bill items")}</AlertDialogTitle>
          <AlertDialogDescription>
            ({selectedItems?.length}){translate("Items will be deleted")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className='gap-2'>
          <AlertDialogCancel asChild>
            <Button size={"sm"} className='text-primary'>
              {translate("Cancel")}
            </Button>
          </AlertDialogCancel>
          <AlertDialogAction asChild onClick={() => handleDeleteBillItemMutation.mutate()}>
            <Button size={"sm"} className='bg-destructive'>
              {translate("Delete")}
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DeleteBillItems;
